import React, { useState, useEffect } from 'react'
import {
  Box,
  TextField,
  Typography,
  Checkbox,
  FormControlLabel,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material'
import { useTranslation } from 'react-i18next'
import Button from '@hapana/shared/web/components/shared/button/button.component'
import {
  BUTTON_VARIANTS,
  BUTTON_THEME,
  BUTTON_SIZE,
} from '@hapana/shared/web/components/shared/button/button.constants'
import posthog from 'posthog-js'
import { useSupportAssistant } from '../support-assistant.hooks'
import { useMutation, Mutation } from '@hapana/shared/web/services/gqty'
import { ISSUE_TYPES } from '../constants/issue-types'
import { Screenshot } from '../types/screenshot.types'
import { ScreenshotUploader } from './screenshot-uploader'

export function SupportTab({ fullWidth = false }: { fullWidth?: boolean }) {
  const { t } = useTranslation('core')
  const {
    messages,
    chatId,
    addTicketMessage,
    planTier,
    userRole,
    pathname,
    pageName,
    brandId,
    brandName,
    locationId,
    locationName,
    userInfo,
    prefillDescription,
    setPrefillDescription,
    prefillIssueType,
    setPrefillIssueType,
  } = useSupportAssistant()

  // Form state
  const [issueType, setIssueType] = useState('')
  const [description, setDescription] = useState('')

  // Consume prefilled fields from assistant tab
  useEffect(() => {
    if (prefillDescription) {
      setDescription(prefillDescription)
      setPrefillDescription(null)
    }
  }, [prefillDescription, setPrefillDescription])

  useEffect(() => {
    if (prefillIssueType) {
      setIssueType(prefillIssueType)
      setPrefillIssueType(null)
    }
  }, [prefillIssueType, setPrefillIssueType])
  const [screenshots, setScreenshots] = useState<Screenshot[]>([])
  const [includeChatHistory, setIncludeChatHistory] = useState(true)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState(false)

  // GraphQL mutation
  const [createTicket] = useMutation(
    (mutation: Mutation, args) => {
      const result = mutation.createSupportTicket(args)

      if (result) {
        void result.ticketId
        void result.ticketUrl
        void result.contactId
      }

      return result
    },
    {
      onComplete: (data) => {
        console.log('Ticket created successfully:', data)
        setIsSubmitting(false)
        // Use first 50 chars of description as subject for the ticket message
        const generatedSubject =
          description.substring(0, 50) + (description.length > 50 ? '...' : '')
        addTicketMessage(generatedSubject, description)
        setSuccess(true)

        // Reset form after delay
        setTimeout(() => {
          setDescription('')
          setIssueType('')
          setScreenshots([])
          setIncludeChatHistory(true)
          setSuccess(false)
        }, 3000)
      },
      onError: (err) => {
        console.error('Failed to create ticket:', err)
        setError(t('supportAssistant.errors.ticketCreationFailed'))
        setIsSubmitting(false)
      },
    }
  )

  const handleSubmit = async () => {
    if (!description.trim()) {
      setError(t('supportAssistant.errors.descriptionRequired'))
      return
    }
    if (!userInfo?.firstName || !userInfo?.lastName || !userInfo?.email) {
      setError(t('supportAssistant.errors.userInfoMissing'))
      return
    }

    setIsSubmitting(true)
    setError(null)

    // Prepare chat history if included
    const chatHistory =
      includeChatHistory && messages.length > 0
        ? messages.map((msg) => ({
            role: msg.role,
            content: msg.content,
            timestamp: msg.createdAt,
          }))
        : undefined

    // Prepare screenshots for submission
    const screenshotData =
      screenshots.length > 0
        ? screenshots.map((s) => ({
            filename: s.filename,
            contentType: s.contentType,
            base64Data: s.base64Data,
          }))
        : undefined

    // Subject will be generated by AI on the backend
    createTicket({
      args: {
        input: {
          description,
          issueType: issueType || undefined,
          firstName: userInfo.firstName,
          lastName: userInfo.lastName,
          email: userInfo.email,
          userId: userInfo.userId || undefined,
          brandId: brandId || undefined,
          brandName: brandName || undefined,
          locationId: locationId || undefined,
          locationName: locationName || undefined,
          chatId: chatId || undefined,
          chatHistory,
          screenshots: screenshotData,
          userRole,
          planTier,
          pathname,
          pageName,
          currentUrl: window.location.href,
          environment: navigator.userAgent,
          posthogSessionUrl: posthog.get_session_replay_url?.() || undefined,
          posthogDistinctId: posthog.get_distinct_id?.() || undefined,
        },
      },
    })
  }

  if (success) {
    return (
      <Box
        sx={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          px: fullWidth ? 0 : 3,
          py: 3,
          textAlign: 'center',
        }}
      >
        <Alert severity="success" sx={{ mb: 2 }}>
          {t('supportAssistant.ticket.success')}
        </Alert>
        <Typography variant="body2" color="text.secondary">
          {t('supportAssistant.ticket.successDescription')}
        </Typography>
      </Box>
    )
  }

  return (
    <Box
      sx={{
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
      }}
    >
      {/* Scrollable content area */}
      <Box
        sx={{
          flex: 1,
          overflowY: 'auto',
          px: fullWidth ? 0 : 3,
          pt: fullWidth ? 2 : 3,
          pb: 3,
          display: 'flex',
          flexDirection: 'column',
          gap: 3,
        }}
      >
        {!fullWidth && (
          <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '1rem' }}>
            {t('supportAssistant.support.title')}
          </Typography>
        )}

        {error && (
          <Alert severity="error" data-testid="support-form-error">
            {error}
          </Alert>
        )}

        {/* Issue Type */}
        <FormControl fullWidth size="small">
          <InputLabel>{t('supportAssistant.support.issueType')}</InputLabel>
          <Select
            value={issueType}
            label={t('supportAssistant.support.issueType')}
            onChange={(e) => setIssueType(e.target.value)}
            disabled={isSubmitting}
            data-testid="support-issue-type"
          >
            {ISSUE_TYPES.map((type) => (
              <MenuItem key={type.id} value={type.id}>
                {t(`supportAssistant.issueTypes.${type.id}`, type.label)}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* Description */}
        <TextField
          fullWidth
          autoFocus={fullWidth}
          label={t('supportAssistant.ticket.description')}
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          disabled={isSubmitting}
          multiline
          rows={6}
          placeholder={t('supportAssistant.ticket.descriptionPlaceholder')}
          data-testid="support-description"
        />

        {/* Screenshot Uploader */}
        <Box>
          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: 'text.secondary' }}>
            {t('supportAssistant.support.screenshots')} ({t('supportAssistant.support.optional')})
          </Typography>
          <ScreenshotUploader
            screenshots={screenshots}
            onScreenshotsChange={setScreenshots}
            disabled={isSubmitting}
          />
        </Box>

        {/* Include Chat History */}
        {messages.length > 0 && (
          <FormControlLabel
            control={
              <Checkbox
                checked={includeChatHistory}
                onChange={(e) => setIncludeChatHistory(e.target.checked)}
                disabled={isSubmitting}
                data-testid="support-include-chat"
              />
            }
            label={
              <Typography variant="body2">
                {t('supportAssistant.ticket.includeChatHistory')} (
                {t('supportAssistant.ticket.messageCount', { count: messages.length })})
              </Typography>
            }
          />
        )}
      </Box>

      {/* Fixed Submit Button */}
      <Box
        sx={{
          px: fullWidth ? 0 : 3,
          py: 3,
          borderTop: '1px solid',
          borderColor: 'grey.200',
          bgcolor: 'white',
        }}
      >
        <Button
          onClick={handleSubmit}
          disabled={isSubmitting || !description.trim()}
          variant={BUTTON_VARIANTS.CONTAINED}
          theme={BUTTON_THEME.PRIMARY}
          size={BUTTON_SIZE.MEDIUM}
          label={
            isSubmitting
              ? t('supportAssistant.ticket.submitting')
              : t('supportAssistant.ticket.submit')
          }
          fullWidth
          data-testid="support-submit-button"
        />
      </Box>
    </Box>
  )
}
